import React, { useEffect, useMemo, useState } from 'react';
import { Button, Checkbox, Input, Radio, message } from 'antd';
import type { NpiHistory, NpiNoReason, TKlarityPayer } from 'types/credentialing';
import { AllState } from 'constants/common';
import { saveProviderPriorCredentialing } from 'api/credentialing';
import s from './s.module.less';

interface Props {
    npiHistory: NpiHistory;
    npiNoReason: NpiNoReason;
    alreadyCredentialedPayers: string[];
    npiNotSure: boolean;
    npiOtherPayers: string;
    onNpiHistoryChange: (v: NpiHistory) => void;
    onNpiNoReasonChange: (v: NpiNoReason) => void;
    onAlreadyCredentialedChange: (payers: string[]) => void;
    onNpiNotSureChange: (v: boolean) => void;
    onNpiOtherPayersChange: (v: string) => void;
    onContinue: () => void;
    payerOptions: TKlarityPayer[];
    providerStates: string[];
    providerId?: number;
}

const NO_REASONS: { value: NpiNoReason; label: string }[] = [
    { value: 'never', label: 'Never credentialed with anyone' },
    {
        value: 'company',
        label: 'Credentialed through a company like Headway, Alma, Grow Therapy, or SonderMind, under their contracts',
    },
    { value: 'hospital', label: 'Credentialed with a hospital or another group practice' },
];

const getStateName = (code: string) => AllState.find((i) => i.dictKey === code)?.content || code;

const NpiHistoryStep = ({
    npiHistory,
    npiNoReason,
    alreadyCredentialedPayers,
    npiNotSure,
    npiOtherPayers,
    onNpiHistoryChange,
    onNpiNoReasonChange,
    onAlreadyCredentialedChange,
    onNpiNotSureChange,
    onNpiOtherPayersChange,
    onContinue,
    payerOptions,
    providerStates,
    providerId,
}: Props) => {
    const activePayers = useMemo(
        () => payerOptions.filter((p) => providerStates.includes(p.state)),
        [payerOptions, providerStates],
    );

    const states = useMemo(() => {
        const set = new Set(activePayers.map((p) => p.state));
        return providerStates.filter((st) => set.has(st)).sort();
    }, [activePayers, providerStates]);

    const isMulti = states.length > 1;
    const [activeState, setActiveState] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setActiveState(isMulti ? states[0] : null);
    }, [isMulti, states]);

    const currentState = (() => {
        if (!isMulti) return states[0];
        return activeState && states.includes(activeState) ? activeState : states[0];
    })();
    const statePayers = useMemo(
        () => (currentState ? activePayers.filter((p) => p.state === currentState) : []),
        [activePayers, currentState],
    );

    const handlePayerCheck = (selKey: string, checked: boolean) => {
        if (checked) {
            onAlreadyCredentialedChange([...alreadyCredentialedPayers, selKey]);
            if (npiNotSure) onNpiNotSureChange(false);
        } else {
            onAlreadyCredentialedChange(alreadyCredentialedPayers.filter((k) => k !== selKey));
        }
    };

    const handleNotSure = (checked: boolean) => {
        onNpiNotSureChange(checked);
        if (checked) {
            onAlreadyCredentialedChange([]);
            onNpiOtherPayersChange('');
        }
    };

    const showOtherInput = npiOtherPayers !== '';

    const handleOtherCheck = (checked: boolean) => {
        if (!checked) onNpiOtherPayersChange('');
        else onNpiOtherPayersChange(' ');
        if (checked && npiNotSure) onNpiNotSureChange(false);
    };

    const canContinue = (() => {
        if (npiHistory === 'direct')
            return alreadyCredentialedPayers.length > 0 || npiNotSure || npiOtherPayers.trim().length > 0;
        if (npiHistory === 'no') return !!npiNoReason;
        return false;
    })();

    const handleContinue = async () => {
        setSaving(true);
        try {
            let dto;
            if (npiHistory !== 'direct') {
                dto = {
                    providerId,
                    priorCredentialed: false,
                    priorNoReason: NO_REASONS.find((r) => r.value === npiNoReason)?.label || '',
                };
            } else {
                const byStateMap: Record<string, string[]> = {};
                alreadyCredentialedPayers.forEach((payerId) => {
                    const payer = activePayers.find((p) => String(p.id) === payerId);
                    if (payer) {
                        if (!byStateMap[payer.state]) byStateMap[payer.state] = [];
                        byStateMap[payer.state].push(payerId);
                    }
                });
                dto = {
                    providerId,
                    priorCredentialed: true,
                    priorYesPayers: {
                        byState: Object.entries(byStateMap).map(([state, payers]) => ({ state, payers })),
                        notSure: npiNotSure,
                        otherPayers: npiOtherPayers.trim(),
                    },
                };
            }
            const res = await saveProviderPriorCredentialing(dto);
            if (res && !res.error) {
                onContinue();
            } else {
                message.error(res?.error || 'Failed to save');
            }
        } catch (e) {
            console.error(e);
            message.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className={s.modalBody}>
                <div className={s.optionList}>
                    <label className={s.optionItem}>
                        <Radio
                            checked={npiHistory === 'no'}
                            onChange={() => {
                                onNpiHistoryChange('no');
                                onAlreadyCredentialedChange([]);
                                onNpiNotSureChange(false);
                                onNpiOtherPayersChange('');
                            }}
                        />
                        <span>No, not yet</span>
                    </label>

                    {npiHistory === 'no' && (
                        <div className={s.subOptions}>
                            <label className={s.subOptionItem}>
                                <Radio
                                    checked={npiNoReason === 'never'}
                                    onChange={() => onNpiNoReasonChange('never')}
                                />
                                <span>Never credentialed with anyone</span>
                            </label>
                            <label className={s.subOptionItem}>
                                <Radio
                                    checked={npiNoReason === 'company'}
                                    onChange={() => onNpiNoReasonChange('company')}
                                />
                                <span>
                                    I&apos;ve been credentialed through a company like Headway, Alma, Grow Therapy, or
                                    SonderMind, under their contracts
                                </span>
                            </label>
                            <label className={s.subOptionItem}>
                                <Radio
                                    checked={npiNoReason === 'hospital'}
                                    onChange={() => onNpiNoReasonChange('hospital')}
                                />
                                <span>I&apos;ve been credentialed with a hospital or group practice</span>
                            </label>
                        </div>
                    )}

                    <label className={s.optionItem}>
                        <Radio
                            checked={npiHistory === 'direct'}
                            onChange={() => {
                                onNpiHistoryChange('direct');
                                onNpiNoReasonChange('');
                            }}
                        />
                        <span>Yes, credentialed under my own NPI directly with payers</span>
                    </label>
                </div>

                {npiHistory === 'direct' && (
                    <>
                        <div className={s.infoBox}>
                            If you&apos;re already credentialed directly under your own NPI with a payer, that panel is
                            already active and we can&apos;t re-do it for you. You can still continue and select any
                            payers you haven&apos;t credentialed with yet.
                        </div>

                        <div className={s.acPayersSection}>
                            <div className={s.acPayersLabel}>Which payers have you already credentialed with?</div>

                            {isMulti && (
                                <>
                                    <div className={s.stateChips}>
                                        {states.map((st) => (
                                            <span
                                                key={st}
                                                className={`${s.stateChip} ${st === currentState ? s.stateChipActive : ''}`}
                                                onClick={() => setActiveState(st)}
                                            >
                                                {getStateName(st)}
                                            </span>
                                        ))}
                                    </div>
                                    {currentState && (
                                        <div className={s.sectionLabel}>
                                            Available now in {getStateName(currentState)}
                                        </div>
                                    )}
                                </>
                            )}

                            <div className={s.payerGrid}>
                                {statePayers.map((p) => {
                                    const sel = String(p.id);
                                    const checked = alreadyCredentialedPayers.includes(sel);
                                    return (
                                        <label key={sel} className={s.payerCheckCell}>
                                            <Checkbox
                                                checked={checked}
                                                onChange={(e) => handlePayerCheck(sel, e.target.checked)}
                                            />
                                            <span>{p.name}</span>
                                        </label>
                                    );
                                })}
                                <label className={s.payerCheckCell}>
                                    <Checkbox
                                        checked={showOtherInput}
                                        onChange={(e) => handleOtherCheck(e.target.checked)}
                                    />
                                    <span>Other</span>
                                </label>
                                <label className={s.payerCheckCell}>
                                    <Checkbox checked={npiNotSure} onChange={(e) => handleNotSure(e.target.checked)} />
                                    <span>I&apos;m not sure</span>
                                </label>
                            </div>

                            {showOtherInput && (
                                <Input
                                    placeholder="Enter payer name(s)"
                                    value={npiOtherPayers.trim() ? npiOtherPayers : ''}
                                    onChange={(e) => onNpiOtherPayersChange(e.target.value)}
                                    style={{ marginTop: 8 }}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>

            <div className={s.modalFooter}>
                <Button
                    type="primary"
                    block
                    size="large"
                    className={s.primaryBtn}
                    disabled={!canContinue}
                    loading={saving}
                    onClick={handleContinue}
                >
                    Continue
                </Button>
            </div>
        </>
    );
};

export default NpiHistoryStep;
